import { ConnectionType } from 'modules/core/supabase/tables/match-chronology';

export type ConnectionIndicatorState = 'good' | 'bad' | 'very_bad' | 'lost' | 'reconnecting';

export interface ConnectionQualityInput {
  /** MatchChronology channel subscription status */
  status: ConnectionType;
  prevStatus: ConnectionType | undefined;
  /** Browser online/offline flag */
  isOnline: boolean;
  /** Broadcast channel subscription status (optional) */
  broadcastStatus?: ConnectionType;
  /** Last measured ping-pong RTT in ms; null = not yet measured (optional) */
  pingLatencyMs?: number | null;
  /** Whether the ping-pong utility received the last pong in time (optional) */
  pingIsAlive?: boolean;
  /** Phoenix socket-level state from RealtimeSocketMonitor (optional) */
  socketConnected?: boolean;
}

/**
 * Analyzes the current connection quality from multiple Supabase signals and
 * returns a unified indicator state.  Abstracted as a pure function for easy
 * extension and testing.
 *
 * Priority (highest → lowest):
 *  1. Browser offline → lost
 *  2. Phoenix socket closed/closing → lost (unless reconnecting)
 *  3. Ping-pong timed out → very_bad
 *  4. High latency (>2 s) → bad
 *  5. Broadcast channel error/closed → bad
 *  6. matchChronology channel status
 *
 * Note: VPN and mobile tethering are considered — TIMED_OUT is treated as
 * degraded quality rather than a full loss, since packets may still arrive
 * slowly through indirect routes.
 */
export function analyzeConnectionQuality(input: ConnectionQualityInput): ConnectionIndicatorState {
  const {
    status,
    prevStatus,
    isOnline,
    broadcastStatus,
    pingLatencyMs,
    pingIsAlive,
    socketConnected,
  } = input;

  // 1. Browser reports no network at all
  if (!isOnline) return 'lost';

  // 2. Phoenix socket is not open
  if (socketConnected === false) {
    if (prevStatus === 'SUBSCRIBED') return 'reconnecting';
    return 'lost';
  }

  // 3. Ping-pong timed out → confirmed unreachable at realtime level
  if (pingIsAlive === false) return 'very_bad';

  // 4. High latency measured by ping-pong
  if (pingLatencyMs != null && pingLatencyMs > 2000) return 'bad';

  // 5. Broadcast channel degraded
  if (broadcastStatus === 'CHANNEL_ERROR') return 'very_bad';
  if (broadcastStatus === 'TIMED_OUT') return 'bad';
  if (broadcastStatus === 'CLOSED' && status === 'SUBSCRIBED') return 'bad';

  // 6. matchChronology channel status (primary signal)
  switch (status) {
    case 'SUBSCRIBED':
      return 'good';

    case 'TIMED_OUT':
      if (prevStatus === 'CLOSED') return 'reconnecting';
      return 'bad';

    case 'CHANNEL_ERROR':
      return 'very_bad';

    case 'CLOSED':
      if (prevStatus === 'SUBSCRIBED') return 'reconnecting';
      return 'lost';

    default:
      return 'good';
  }
}
